import React from 'react';
import { colors, borderRadius, shadows, fontSize, fontWeight, transitions } from '../../constants/theme';

interface CardProps {
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  variant?: 'default' | 'bordered' | 'elevated';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  hoverable?: boolean;
  onClick?: () => void;
  headerAction?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

const Card: React.FC<CardProps> = ({
  title,
  subtitle,
  children,
  variant = 'default',
  padding = 'md',
  hoverable = false,
  onClick,
  headerAction,
  footer,
  className = '',
  style
}) => {
  const [isHovered, setIsHovered] = React.useState(false);

  const paddingMap = {
    none: '0',
    sm: '12px',
    md: '20px',
    lg: '28px'
  };

  const variantStyles = {
    default: {
      backgroundColor: colors.ui.bg,
      border: `1px solid ${colors.ui.border}`,
      boxShadow: 'none'
    },
    bordered: {
      backgroundColor: colors.ui.bg,
      border: `2px solid ${colors.ui.border}`,
      boxShadow: 'none'
    },
    elevated: {
      backgroundColor: colors.ui.bg,
      border: 'none',
      boxShadow: shadows.md
    }
  };

  const cardStyles: React.CSSProperties = {
    borderRadius: borderRadius.lg,
    transition: `all ${transitions.base}`,
    cursor: onClick || hoverable ? 'pointer' : 'default',
    overflow: 'hidden',
    ...variantStyles[variant],
    ...(isHovered && (onClick || hoverable) && {
      transform: 'translateY(-2px)',
      boxShadow: variant === 'elevated' ? shadows.xl : shadows.lg,
      borderColor: variant !== 'elevated' ? colors.accent.blue : undefined
    }),
    ...style
  };

  const headerStyles: React.CSSProperties = {
    padding: paddingMap[padding],
    paddingBottom: title || subtitle ? '16px' : paddingMap[padding],
    borderBottom: title || subtitle ? `1px solid ${colors.ui.border}` : 'none'
  };

  const titleStyles: React.CSSProperties = {
    fontSize: fontSize.lg,
    fontWeight: fontWeight.semibold,
    color: colors.text.primary,
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  };

  const subtitleStyles: React.CSSProperties = {
    fontSize: fontSize.sm,
    color: colors.text.secondary,
    marginTop: '4px'
  };

  const contentStyles: React.CSSProperties = {
    padding: paddingMap[padding],
    paddingTop: title || subtitle ? paddingMap[padding] : '0'
  };

  const footerStyles: React.CSSProperties = {
    padding: paddingMap[padding],
    paddingTop: '16px',
    borderTop: `1px solid ${colors.ui.border}`,
    backgroundColor: colors.ui.bgSecondary
  };

  const handleMouseEnter = () => {
    if (onClick || hoverable) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return (
    <div
      className={className}
      style={cardStyles}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {(title || subtitle) && (
        <div style={headerStyles}>
          <div style={titleStyles}>
            <div>
              <h3 style={{ margin: 0, fontSize: fontSize.lg, fontWeight: fontWeight.semibold }}>
                {title}
              </h3>
              {subtitle && (
                <p style={subtitleStyles}>
                  {subtitle}
                </p>
              )}
            </div>
            {headerAction && (
              <div style={{ marginLeft: '16px' }}>
                {headerAction}
              </div>
            )}
          </div>
        </div>
      )}
      
      <div style={contentStyles}>
        {children}
      </div>

      {footer && (
        <div style={footerStyles}>
          {footer}
        </div>
      )}
    </div>
  );
};

export default Card;
export { Card };